/**
 * Self-contained "I want to mint a bearer token for this app" section.
 *
 * Shared between the manager Tokens page (/apps/:slug/tokens) and the
 * non-manager AppDetail overview (/apps/:slug). Each user mints their
 * own tokens — non-managers are first-class citizens here, they just
 * don't see the all-tokens admin view.
 *
 * The section owns the local form state (label, expiry, the one-time
 * reveal). The parent supplies the app row, `myTokens`, and the shared
 * busy / message state so error and loading UI stay consistent with the
 * rest of the page.
 */

import { useState } from 'react';
import { api } from '../api/client.js';
import type { AppSummary, AppTokenSummary, MintTokenResponse } from '../api/types.js';
import { IconCopy, IconKey, IconPlug, IconShield, IconTrash, IconX } from './Icons.js';
import { Banner, Button, Card, CardHeader, EmptyState, Field, Mono, inputClass } from './ui.js';
import { relTime } from '../lib/format.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';

const EXPIRY_OPTIONS: { days: number; label: string }[] = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days (recommended)' },
  { days: 180, label: '180 days' },
  { days: 365, label: '1 year (max)' },
];

export interface UserTokenSectionProps {
  app: Pick<AppSummary, 'slug' | 'name' | 'bearer_paths' | 'cf_custom_domain_live'>;
  myTokens: AppTokenSummary[];
  busy: boolean;
  setBusy: (busy: boolean) => void;
  setActionMessage: (msg: ActionMessage | null) => void;
  reload: () => Promise<void>;
  /** Optional override for the "Your tokens" card title — defaults to "Your tokens". */
  listTitle?: string;
  /** When true, render the plaintext-reveal warning banner inline above the cards. */
  showPlaintextBanner?: boolean;
}

export function UserTokenSection({
  app,
  myTokens,
  busy,
  setBusy,
  setActionMessage,
  reload,
  listTitle = 'Your tokens',
  showPlaintextBanner = true,
}: UserTokenSectionProps) {
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [revealed, setRevealed] = useState<MintTokenResponse | null>(null);
  const [copyConfirm, setCopyConfirm] = useState(false);

  const bearerPaths = app.bearer_paths ?? [];
  const enabled = bearerPaths.length > 0;
  const appHost = app.cf_custom_domain_live ?? `${app.slug}.mkin.app`;

  async function generate(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    await runAction({
      do: () =>
        api.post<MintTokenResponse>(`/api/apps/${encodeURIComponent(app.slug)}/tokens`, {
          label: label.trim(),
          expiresInDays,
        }),
      onSuccess: (res) => {
        setRevealed(res);
        setLabel('');
        return `Token created. Copy it now — you won't see it again.`;
      },
      errorPrefix: "Couldn't create token",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function revoke(t: AppTokenSummary) {
    if (!confirm(`Revoke "${t.label}"? Any client using this token will lose access immediately.`))
      return;
    await runAction({
      do: () => api.delete(`/api/apps/${encodeURIComponent(app.slug)}/tokens/${t.id}`),
      onSuccess: () => `Revoked "${t.label}".`,
      errorPrefix: "Couldn't revoke",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  function copyToken() {
    if (!revealed) return;
    void navigator.clipboard.writeText(revealed.token).then(() => {
      setCopyConfirm(true);
      setTimeout(() => setCopyConfirm(false), 1500);
    });
  }

  return (
    <div className="space-y-5">
      {showPlaintextBanner && (
        <Banner tone="info">
          <span className="inline-flex items-center gap-1.5">
            <IconShield size={14} />
            Plaintext tokens are shown exactly once. Save them at creation; the server never returns
            them again.
          </span>
        </Banner>
      )}

      {revealed && (
        <Card>
          <CardHeader
            title="New token — copy it now"
            description="This is the only time you'll see the plaintext. After you dismiss this, only metadata is shown."
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRevealed(null)}
                aria-label="Dismiss"
              >
                <IconX size={13} />
                Done
              </Button>
            }
          />
          <div className="px-5 py-4 space-y-4">
            <div className="flex items-center gap-2">
              <Mono className="flex-1 break-all px-3 py-2 rounded-md bg-surface-2 border border-line text-xs">
                {revealed.token}
              </Mono>
              <Button variant="primary" size="sm" onClick={copyToken}>
                <IconCopy size={13} />
                {copyConfirm ? 'Copied' : 'Copy'}
              </Button>
            </div>
            {enabled && (
              <div className="space-y-2 text-xs">
                <p className="text-muted font-medium uppercase tracking-wider text-[10px]">
                  Connect Claude Code
                </p>
                <Mono className="block break-all px-3 py-2 rounded-md bg-surface-2 border border-line">
                  claude mcp add {app.slug} --transport http https://{appHost}/mcp --header
                  &quot;Authorization: Bearer {revealed.token.slice(0, 8)}…&quot;
                </Mono>
                <p className="text-muted font-medium uppercase tracking-wider text-[10px] pt-2">
                  Or curl
                </p>
                <Mono className="block break-all px-3 py-2 rounded-md bg-surface-2 border border-line">
                  curl -H &quot;Authorization: Bearer {revealed.token.slice(0, 8)}…&quot; https://
                  {appHost}
                  {bearerPaths.find((p) => !p.includes('mcp')) ?? bearerPaths[0]}
                </Mono>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title="External access"
          description={
            enabled
              ? 'Tokens authenticate requests to these paths on the app:'
              : 'No paths have been opened to bearer-token access yet.'
          }
        />
        <div className="px-5 py-4 text-sm">
          {enabled ? (
            <ul className="font-mono text-xs space-y-1.5">
              {bearerPaths.map((p) => (
                <li
                  key={p}
                  className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-surface-2 border border-line mr-2"
                >
                  <IconPlug size={11} className="text-faint" />
                  {p}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted">
              Ask the AI agent that built this app to enable external access — for example, “add an
              MCP server at <Mono>/mcp</Mono>”. Until paths are declared, tokens can&apos;t reach
              anything on this app.
            </p>
          )}
        </div>
      </Card>

      {enabled && (
        <Card>
          <CardHeader
            title="Generate a token"
            description="One token, one client. Label it so you can revoke the right one later."
          />
          <form
            onSubmit={(e) => void generate(e)}
            className="px-5 py-4 grid sm:grid-cols-[1fr_200px_auto] gap-3 items-end"
          >
            <Field label="Label" required>
              <input
                type="text"
                required
                maxLength={64}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Claude Code on my laptop"
                className={inputClass}
              />
            </Field>
            <Field label="Expires">
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                className={inputClass}
              >
                {EXPIRY_OPTIONS.map((o) => (
                  <option key={o.days} value={o.days}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={busy}
              disabled={busy || !label.trim()}
            >
              Generate
            </Button>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader title={listTitle} description={`Tokens you've minted for ${app.name}.`} />
        {myTokens.length === 0 ? (
          <EmptyState
            icon={<IconKey size={18} />}
            title="No tokens yet"
            description={
              enabled
                ? 'Click Generate above to create one.'
                : 'External access is disabled, so there’s nothing to mint a token for.'
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {myTokens.map((t) => (
              <li key={t.id} className="px-5 py-3 text-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Mono className="font-medium">{t.label}</Mono>
                      {t.revoked_at && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-danger-soft text-danger font-medium uppercase tracking-wide">
                          revoked
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted">
                      created {relTime(t.created_at)} ·{' '}
                      {t.last_used_at ? `last used ${relTime(t.last_used_at)}` : 'never used'} ·{' '}
                      {t.revoked_at
                        ? `revoked ${relTime(t.revoked_at)}`
                        : `expires ${relTime(t.expires_at)}`}
                    </p>
                  </div>
                  {!t.revoked_at && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void revoke(t)}
                      disabled={busy}
                      className="!text-danger hover:!bg-danger-soft"
                    >
                      <IconTrash size={13} />
                      Revoke
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
